<template>
  <header class="header">
    <div class="left-icon">
      <button class="header-button header-button--icon" type="button" data-testid="header-reload"
              :aria-label="$t('message.reload')" @click="$emit('reload-page')">
        <BzIcon v-if="systemLane" name="refresh" />
        <i v-else class="pi pi-refresh" aria-hidden="true"></i>
      </button>
      <button
        v-if="hasRiderActivity"
        class="header-button rider-counter"
        type="button"
        :class="{ alert: ridersAlert, urgent: ridersUrgent }"
        :aria-label="$t('message.ridersWaiting', waitingRidersCount)"
        @click="$emit('toggle-rider-list')"
      >
        <BzIcon v-if="systemLane" name="delivery_dining" class="rider-counter-icon" />
        <svg v-else class="rider-counter-icon" viewBox="0 0 24 24" fill="currentColor" shape-rendering="geometricPrecision" aria-hidden="true" focusable="false">
          <path d="M19 7c0-1.1-.9-2-2-2h-3v2h3v2.65L13.52 14H10V9H6c-2.21 0-4 1.79-4 4v3h2c0 1.66 1.34 3 3 3s3-1.34 3-3h4.48L19 10.35V7zM7 17c-.55 0-1-.45-1-1h2c0 .55-.45 1-1 1zm10-2c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zm0 4c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>
        </svg>
        <span v-if="waitingRidersCount > 0" class="rider-counter-number" aria-hidden="true">{{ waitingRidersCount }}</span>
      </button>
    </div>
    <img alt="Bronze Logo" class="logo" src="@/assets/logo.svg"/>
    <div class="right-icons">
      <button
        class="header-button help-button"
        type="button"
        data-testid="open-help"
        :aria-label="$t('message.help')"
        @click="$emit('open-help')"
      >
        <BzIcon v-if="systemLane" name="headset_mic" class="help-icon" />
        <svg
          v-else
          class="help-icon"
          viewBox="0 0 24 24"
          fill="currentColor"
          shape-rendering="geometricPrecision"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M3 12.2A9 9 0 0 1 21 12.2H18.8A6.8 6.8 0 0 0 5.2 12.2ZM4.6 11.4A2 2 0 0 1 6.6 13.4V16.2A2 2 0 0 1 4.6 18.2A2 2 0 0 1 2.6 16.2V13.4A2 2 0 0 1 4.6 11.4ZM19.4 11.4A2 2 0 0 1 21.4 13.4V16.2A2 2 0 0 1 19.4 18.2A2 2 0 0 1 17.4 16.2V13.4A2 2 0 0 1 19.4 11.4ZM21.4 16.6V18.6A2.6 2.6 0 0 1 18.8 21.2H16.5V19.2H18.8A0.6 0.6 0 0 0 19.4 18.6V16.6ZM14.5 18.9H15.5A1.3 1.3 0 0 1 15.5 21.5H14.5A1.3 1.3 0 0 1 14.5 18.9Z"/>
        </svg>
        <span class="help-label">{{ $t('message.help') }}</span>
      </button>
      <button class="header-button header-button--icon" type="button" data-testid="header-settings"
              :aria-label="$t('message.settings')" @click="$emit('open-settings')">
        <BzIcon v-if="systemLane" name="settings" />
        <i v-else class="pi pi-cog" aria-hidden="true"></i>
      </button>
    </div>
  </header>
</template>

<script>
import BzIcon from '../ui/components/BzIcon.vue';
import { systemLane } from '../ui/lane';

export default {
  name: 'HeaderComponent',
  components: { BzIcon },
  emits: ['reload-page', 'open-help', 'open-settings', 'toggle-rider-list'],
  props: {
    waitingRidersCount: {
      type: Number,
      default: 0
    },
    hasRiderActivity: {
      type: Boolean,
      default: false
    },
    ridersAlert: {
      type: Boolean,
      default: false
    },
    ridersUrgent: {
      type: Boolean,
      default: false
    }
  },
  data() {
    // The service screen this page was painted with (src/ui/lane.js): fixed for the page's life.
    return { systemLane: systemLane() };
  }
}
</script>

<style scoped>
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 64px;
  background-color: var(--bz-p-0c1117);
  color: var(--bz-p-white);
  padding: 0 10px;
}

.logo {
  max-height: 25px;
  margin: 0 10px;
  flex-shrink: 0;
}

.header-button {
  cursor: pointer;
  background: linear-gradient(var(--bz-p-393e43), var(--bz-p-393e43)) padding-box, 
              linear-gradient(to bottom right, var(--bz-p-40454a), var(--bz-p-5c6065)) border-box;
  border: 1px solid transparent;
  border-radius: 9px;
  color: var(--bz-p-edf0f3);
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (hover: hover) {
  .header-button:hover {
    background: linear-gradient(var(--bz-p-40454a), var(--bz-p-40454a)) padding-box,
                linear-gradient(to bottom right, var(--bz-p-4a4f54), var(--bz-p-66696e)) border-box;
  }
}

.left-icon, .right-icons {
  flex: 1;
  display: flex;
  align-items: center;
}

.left-icon {
  justify-content: flex-start;
  gap: 8px;
}

.right-icons {
  justify-content: flex-end;
  gap: 8px;
}

/* Rider counter button — matches the height of the other header buttons */
.rider-counter {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

/* Specificity: beat the `.header-button { padding: 6px 12px }` shorthand below so the vertical padding actually shrinks */
.header-button.rider-counter {
  padding-top: 5px;
  padding-bottom: 5px;
}

.rider-counter-number {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace;
  font-weight: 500;
  font-size: 16px;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

/* Alert state: red liquid-glass — shown whenever there's at least one un-photoed order with a waiting rider */
.rider-counter.alert {
  background: linear-gradient(var(--bz-p-red-700), var(--bz-p-red-700)) padding-box,
              linear-gradient(to bottom right, var(--bz-p-red-600), var(--bz-p-red-500)) border-box;
  color: var(--bz-p-white);
}

@media (hover: hover) {
  .rider-counter.alert:hover {
    background: linear-gradient(var(--bz-p-red-800), var(--bz-p-red-800)) padding-box,
                linear-gradient(to bottom right, var(--bz-p-red-600), var(--bz-p-red-400)) border-box;
  }
}

/* Urgent state (riders waiting ≥10 min): no longer a pulse. Norm 1 (nothing loops beside the photo) and
   docs/sistema-diseno/forma-y-tamano.md, step 1: the red fill and the order card's band say it, fixed. */

.rider-counter-icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  shape-rendering: geometricPrecision;
}

/* Help button — the only amber surface in the header. It is an invitation, not
   an alarm: no looping animation. Amber tokens are the ones already in use in
   PhotoCoverageAlertComponent (#F5C451 / #281A02).
   Same "liquid glass" language as the other header buttons: flat fill in
   padding-box, diagonal light gradient in border-box. The bevel is shallower
   than the grey and red ones on purpose — the amber fill already sits at
   luminance 0.595, so even pure white would only reach 1.63:1 against it, while
   grey and red get 1.71:1 because they start much darker.
   Note there is no border-color here: the transparent 1px border inherited from
   .header-button is what lets the border-box gradient show through. */
.header-button.help-button {
  position: relative; /* anchor for the touch-target expander below */
  background: linear-gradient(var(--bz-p-f5c451), var(--bz-p-f5c451)) padding-box,
              linear-gradient(to bottom right, var(--bz-p-fdcd5f), var(--bz-p-ffda8a)) border-box;
  color: var(--bz-p-281a02);
  gap: 7px;
  padding: 6px 10px;
  /* Mobile drops the icon, so the 15px label alone would shrink the button to
     29px and break the row. Buttons compute as border-box (UA stylesheet), so
     this is the total height — matching the other three exactly. */
  min-height: 34px;
  font-family: "DM Sans", system-ui, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  flex-shrink: 0;
}

/* No hover state of its own. On the kitchen tablets Android keeps :hover stuck
   after a tap, so any distinct hover colour becomes a permanent one. This repeats
   the full two-layer declaration because a flat colour here would wipe the bevel
   on any device with a real pointer (only there: the generic hover above is too). */
@media (hover: hover) {
  .header-button.help-button:hover {
    background: linear-gradient(var(--bz-p-f5c451), var(--bz-p-f5c451)) padding-box,
                linear-gradient(to bottom right, var(--bz-p-fdcd5f), var(--bz-p-ffda8a)) border-box;
  }
}

/* Pressed: a polarity flip, not a shift of hue. The amber moves from the fill to
   the border and the content, so the button empties out under the finger while
   staying recognisably the amber help button. It borrows nothing from the order
   palette, where #E8A654 was a dE2000 of 0.83 from the "incorrect order" card —
   the same colour to the eye. The scale is compositor-only, no transition, and
   deliberately NOT a box-shadow (see the capture-pulse case).
   The bevel disappears here on purpose: in this header the bevel reads as
   volume, so removing it is what says "pushed in" — polarity plus depth in one
   gesture. It also has to be flat for a technical reason: the #281A02 fill only
   makes 1.12:1 against the header, so the solid #F5C451 rim is the only thing
   keeping the button from dissolving (11.64:1); a gradient there would lose
   definition along its darker half.
   Must stay AFTER the :hover rule: same specificity, later one wins, which is
   what stops a stuck :hover from swallowing the pressed state. */
.header-button.help-button:active {
  background: var(--bz-p-281a02);
  border-color: var(--bz-p-f5c451);
  color: var(--bz-p-f5c451);
  transform: scale(0.96);
}

.header-button.help-button:focus-visible {
  outline: 3px solid var(--bz-p-e8e0d4);
  outline-offset: 2px;
}

/* Grows the touch target to 46px without changing the visual height of the
   header. -3px sideways stays clear of the settings button (8px gap at its
   narrowest). */
.header-button.help-button::after {
  content: "";
  position: absolute;
  inset: -6px -3px;
}

/* Below 768px the word carries the button on its own: on a narrow screen an
   unlabelled icon is the thing kitchen staff fail to read, and being understood
   matters more here than the header's width budget. */
.help-icon {
  display: none;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  shape-rendering: geometricPrecision;
}

/* Mobile styles (default) */
.header-button {
  padding: 6px 12px;
  font-size: 20px;
}

.left-icon .header-button {
  margin-left: 8px;
}

.right-icons .header-button:last-child {
  margin-right: 8px;
}

/* Tablet styles */
@media screen and (min-width: 768px) {
  .help-icon {
    display: block;
  }

  .header-button.help-button {
    padding: 6px 14px;
    font-size: 16px;
    gap: 8px;
  }

  .left-icon {
    gap: 12px;
  }

  .header-button {
    padding: 7px 16px;
    font-size: 18px;
  }

  .left-icon .header-button {
    margin-left: 16px;
  }

  .right-icons .header-button:last-child {
    margin-right: 16px;
  }

  .right-icons {
    gap: 12px;
  }
}

/* Desktop styles */
@media screen and (min-width: 1024px) {
  .header-button {
    padding: 8px 20px;
    font-size: 20px;
  }

  .header-button.help-button {
    padding: 7px 18px;
    font-size: 17px;
    gap: 9px;
  }

  .help-icon {
    width: 22px;
    height: 22px;
  }

  .left-icon .header-button {
    margin-left: 32px;
  }

  .right-icons .header-button:last-child {
    margin-right: 32px;
  }

  .right-icons {
    gap: 16px;
  }

  /* Keep the rider counter the same height as the other buttons (desktop = 38 px incl. border) */
  .header-button.rider-counter {
    padding-top: 7px;
    padding-bottom: 7px;
  }
}
/* ---- The service screen on the design system (src/ui/lane.js: `ui-system-lane`, per restaurant) ----
   docs/sistema-diseno/forma-y-tamano.md, «Cabecera»: circles of 48 for the icon-only buttons (Reload,
   Settings) and capsules of 48 for the ones with words (riders, Help), flat on the control grey with the
   hairline edge, 12 apart, on the screen's gutter. Help keeps production's yellow, now the brand's own
   component (`--bz-brand`), far from the traffic light. The rules above stay for the stores without the
   flag: they are production's, byte for byte. */
:root[data-bz-lane="system"] .header {
  height: var(--bz-bar-height);
  padding: 0 var(--bz-gutter);
  background: var(--bz-bg);
  color: var(--bz-ink);
}

:root[data-bz-lane="system"] .left-icon,
:root[data-bz-lane="system"] .right-icons {
  gap: var(--bz-target-gap);
}

:root[data-bz-lane="system"] .header-button {
  box-sizing: border-box;
  min-width: var(--bz-target-min);
  min-height: var(--bz-target-min);
  margin: 0;
  padding: 0 var(--bz-space-4);
  gap: var(--bz-space-2);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-full);
  background: var(--bz-control);
  color: var(--bz-ink);
  font-family: var(--bz-font);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .header-button .bz-icon {
  font-size: var(--bz-icon-md);
}

:root[data-bz-lane="system"] .rider-counter-icon {
  width: 1em;
  height: 1em;
}

:root[data-bz-lane="system"] .header-button--icon {
  width: var(--bz-target-min);
  padding: 0;
}

:root[data-bz-lane="system"] .header-button.rider-counter {
  padding: 0 var(--bz-space-4);
}

:root[data-bz-lane="system"] .rider-counter.alert {
  border-color: transparent;
  background: var(--bz-rider-solid);
  color: var(--bz-on-rider-solid);
}

:root[data-bz-lane="system"] .rider-counter-number {
  font-family: var(--bz-font);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .header-button.help-button,
:root[data-bz-lane="system"] .header-button.help-button:active {
  min-height: var(--bz-target-min);
  padding: 0 var(--bz-space-5);
  border-color: transparent;
  background: var(--bz-brand);
  color: var(--bz-on-brand);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
}

/* 48 already: the touch-target expander of production's 34-px button has nothing left to grow. */
:root[data-bz-lane="system"] .header-button.help-button::after {
  content: none;
}

:root[data-bz-lane="system"] .header-button:focus-visible {
  outline: 3px solid var(--bz-focus);
  outline-offset: 2px;
}

/* A phone: five targets of 48 with 12 between them and the gutters leave no room for the logo once the
   rider counter is there. The logo is the one thing that only decorates, so it is what gives way. */
@media screen and (max-width: 479px) {
  :root[data-bz-lane="system"] .header:has(.rider-counter) .logo {
    display: none;
  }
}

@media screen and (min-width: 768px) {
  :root[data-bz-lane="system"] .help-icon {
    width: 1em;
    height: 1em;
  }
}
</style>